/**
 * Video Storyboard
 *
 * Renders extracted video frames as storyboard and timestamp cards.
 *
 * Primary responsibilities:
 * - Normalize model-provided video frame JSON
 * - Display actual screenshot image URLs from uploaded videos
 * - Keep timestamp-specific screenshots easy to scan and open
 *
 * Dependencies:
 * - Safe media URL helpers
 * - Tabler icons
 */

import React from 'react'
import Image from 'next/image'
import {
  IconExternalLink,
  IconPhoto,
  IconTimeline,
  IconVideo
} from '@tabler/icons-react'

import { getSafeRenderableImageUrl, getSafeMarkdownUrl } from '@/lib/media/safe-url'
import { cn } from '@/lib/utils'

type UnknownRecord = Record<string, unknown>
type VideoStoryboardLayout = 'simple' | 'detailed'

export type VideoStoryboardFrame = {
  id?: string
  videoId?: string
  videoTitle?: string
  videoUrl?: string
  timestamp: string
  seconds?: number
  imageUrl: string
  title?: string
  caption?: string
  description?: string
  transcriptExcerpt?: string
}

export type VideoStoryboardData = {
  title?: string
  description?: string
  layout?: VideoStoryboardLayout
  videoId?: string
  videoTitle?: string
  videoUrl?: string
  frames: VideoStoryboardFrame[]
}

type VideoStoryboardFrameGroup = {
  key: string
  label: string
  videoUrl?: string
  frames: VideoStoryboardFrame[]
}

type VideoStoryboardProps = {
  data: unknown
  className?: string
}

type VideoTimestampProps = {
  data: unknown
  className?: string
}

// --------------

const isRecord = (value: unknown): value is UnknownRecord =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value))

// --------------

const readString = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value !== 'string') continue
    const trimmed = value.trim()
    if (trimmed) return trimmed
  }

  return ''
}

// --------------

const readNumber = (value: unknown) => {
  const numberValue = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(numberValue) ? numberValue : undefined
}

// --------------

const formatSeconds = (seconds: number) => {
  const mins = Math.floor(Math.max(0, seconds) / 60)
  const secs = Math.max(0, seconds) % 60
  return `${String(mins).padStart(2, '0')}:${secs.toFixed(1).padStart(4, '0')}`
}

// --------------

const readImageUrl = (record: UnknownRecord) =>
  getSafeRenderableImageUrl(
    record.imageUrl ??
      record.image_url ??
      record.image ??
      record.url ??
      record.src ??
      record.screenshotUrl ??
      record.screenshot_url
  ) || ''

// --------------

const readFrameItems = (record: UnknownRecord) => {
  if (Array.isArray(record.frames)) return record.frames
  if (Array.isArray(record.items)) return record.items
  if (Array.isArray(record.screenshots)) return record.screenshots
  if (Array.isArray(record.keyframes)) return record.keyframes
  if (Array.isArray(record.storyboard)) return record.storyboard

  return []
}

// --------------

const normalizeLayout = (record: UnknownRecord): VideoStoryboardLayout => {
  const layout = readString(record.layout, record.variant, record.style).toLowerCase()
  return ['simple', 'compact', 'minimal'].includes(layout) ? 'simple' : 'detailed'
}

// --------------

const normalizeFrame = (
  value: unknown,
  index: number,
  parent?: UnknownRecord
): VideoStoryboardFrame | null => {
  if (!isRecord(value)) return null

  const imageUrl = readImageUrl(value)
  if (!imageUrl) return null

  const seconds = readNumber(value.seconds ?? value.time_seconds ?? value.timestamp_seconds)
  const timestamp =
    readString(value.timestamp, value.time, value.at) ||
    (typeof seconds === 'number' ? formatSeconds(seconds) : `Frame ${index + 1}`)

  return {
    id: readString(value.id, value.frameId, value.frame_id) || undefined,
    videoId:
      readString(
        value.videoId,
        value.video_id,
        value.fileId,
        value.file_id,
        parent?.videoId,
        parent?.video_id,
        parent?.fileId,
        parent?.file_id
      ) || undefined,
    videoTitle:
      readString(
        value.videoTitle,
        value.video_title,
        value.videoName,
        value.video_name,
        parent?.videoTitle,
        parent?.video_title,
        parent?.videoName,
        parent?.video_name
      ) ||
      undefined,
    videoUrl:
      getSafeMarkdownUrl(
        value.videoUrl ?? value.video_url ?? parent?.videoUrl ?? parent?.video_url
      ) || undefined,
    timestamp,
    seconds,
    imageUrl,
    title: readString(value.title, value.label) || undefined,
    caption: readString(value.caption, value.summary) || undefined,
    description: readString(value.description, value.notes) || undefined,
    transcriptExcerpt:
      readString(
        value.transcriptExcerpt,
        value.transcript_excerpt,
        value.transcript,
        value.audio
      ) || undefined
  }
}

// --------------

const normalizeStoryboard = (data: unknown): VideoStoryboardData | null => {
  if (!isRecord(data)) return null

  const rawFrames = readFrameItems(data)
  const frames = rawFrames
    .map((frame, index) => normalizeFrame(frame, index, data))
    .filter((frame): frame is VideoStoryboardFrame => Boolean(frame))

  if (frames.length === 0) return null

  return {
    title: readString(data.title, data.name) || 'Video storyboard',
    description: readString(data.description, data.summary) || undefined,
    layout: normalizeLayout(data),
    videoId: readString(data.videoId, data.video_id, data.fileId, data.file_id) || undefined,
    videoTitle:
      readString(data.videoTitle, data.video_title, data.videoName, data.video_name) ||
      undefined,
    videoUrl: getSafeMarkdownUrl(data.videoUrl ?? data.video_url) || undefined,
    frames
  }
}

// --------------

const normalizeTimestamp = (data: unknown): VideoStoryboardFrame | null => {
  const frame = normalizeFrame(data, 0)
  if (frame) return frame

  if (!isRecord(data)) return null
  const nestedFrame = data.frame ?? data.screenshot
  return normalizeFrame(nestedFrame, 0, data)
}

// --------------

const getFrameSourceKey = (frame: VideoStoryboardFrame) =>
  frame.videoId || frame.videoTitle || frame.videoUrl || 'uploaded-video'

// --------------

const getFrameSourceLabel = (frame: VideoStoryboardFrame, index: number) =>
  frame.videoTitle || frame.videoId || `Uploaded video ${index + 1}`

// --------------

const groupFramesByVideo = (frames: VideoStoryboardFrame[]) => {
  const groups: VideoStoryboardFrameGroup[] = []
  const groupIndexes = new Map<string, number>()

  frames.forEach((frame) => {
    const key = getFrameSourceKey(frame)
    const groupIndex = groupIndexes.get(key)

    if (typeof groupIndex === 'number') {
      groups[groupIndex].frames.push(frame)
      return
    }

    groupIndexes.set(key, groups.length)
    groups.push({
      key,
      label: getFrameSourceLabel(frame, groups.length),
      videoUrl: frame.videoUrl,
      frames: [frame]
    })
  })

  return groups
}

// --------------

const formatCount = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`

// --------------

const StoryboardFrameCard = ({
  frame,
  compact = false,
  simple = false
}: {
  frame: VideoStoryboardFrame
  compact?: boolean
  simple?: boolean
}) => {
  const sourceLabel = frame.videoTitle || frame.videoId

  return (
    <figure
      className={cn(
        'border-border bg-background overflow-hidden rounded-lg border',
        compact ? 'max-w-xl' : 'min-w-0'
      )}
    >
      <div className="relative">
        <a
          href={frame.imageUrl}
          target="_blank"
          rel="noreferrer"
          className="bg-muted block aspect-video w-full overflow-hidden"
        >
          <Image
            src={frame.imageUrl}
            alt={frame.title || `Video screenshot at ${frame.timestamp}`}
            width={640}
            height={360}
            className="size-full object-contain"
            unoptimized
          />
        </a>
        <div className="pointer-events-none absolute inset-x-2 top-2 flex items-start justify-between gap-2">
          <span className="bg-background/90 text-foreground border-border rounded border px-2 py-1 font-mono text-[11px] font-medium shadow-sm">
            {frame.timestamp}
          </span>
          {sourceLabel ? (
            <span className="bg-background/90 text-muted-foreground border-border max-w-[62%] truncate rounded border px-2 py-1 text-[11px] shadow-sm">
              {sourceLabel}
            </span>
          ) : null}
        </div>
        <a
          href={frame.imageUrl}
          target="_blank"
          rel="noreferrer"
          className="bg-background/90 text-muted-foreground hover:text-foreground focus-visible:ring-ring border-border absolute bottom-2 right-2 flex min-h-10 min-w-10 items-center justify-center rounded border shadow-sm focus-visible:outline-none focus-visible:ring-2"
          aria-label={`Open screenshot at ${frame.timestamp}`}
        >
          <IconExternalLink size={16} />
        </a>
      </div>
      <figcaption className={cn('space-y-1.5', simple ? 'p-2' : 'p-3')}>
        {sourceLabel ? (
          <div className="text-muted-foreground flex items-center gap-2 text-xs sm:hidden">
            <IconVideo size={14} className="shrink-0" />
            <span className="truncate">{sourceLabel}</span>
          </div>
        ) : null}
        {frame.title ? (
          <div className={cn('font-medium leading-snug', simple ? 'text-xs' : 'text-sm')}>
            {frame.title}
          </div>
        ) : null}
        {frame.caption ? (
          <p className={cn('text-muted-foreground leading-snug', simple ? 'text-xs' : 'text-sm')}>
            {frame.caption}
          </p>
        ) : null}
        {!simple && frame.description ? (
          <p className="text-muted-foreground text-xs leading-snug">{frame.description}</p>
        ) : null}
        {!simple && frame.transcriptExcerpt ? (
          <blockquote className="border-border text-muted-foreground border-l-2 pl-2 text-xs leading-snug">
            {frame.transcriptExcerpt}
          </blockquote>
        ) : null}
        {!simple && frame.videoUrl ? (
          <a
            href={frame.videoUrl}
            target="_blank"
            rel="noreferrer"
            className="text-muted-foreground hover:text-foreground focus-visible:ring-ring inline-flex min-h-8 items-center gap-1.5 text-xs focus-visible:outline-none focus-visible:ring-2"
          >
            <IconVideo size={14} />
            Open source video
          </a>
        ) : null}
      </figcaption>
    </figure>
  )
}

// --------------

export const VideoStoryboard: React.FC<VideoStoryboardProps> = ({ data, className }) => {
  const storyboard = normalizeStoryboard(data)
  if (!storyboard) return null

  const groups = groupFramesByVideo(storyboard.frames)
  const sourceCount = groups.length
  const isSimple = storyboard.layout === 'simple'

  return (
    <section
      className={cn(
        'border-border bg-card my-4 overflow-hidden rounded-lg border',
        isSimple ? 'shadow-none' : 'shadow-sm',
        className
      )}
    >
      <header className={cn('border-border flex items-start justify-between gap-3 border-b', isSimple ? 'p-2.5' : 'p-3')}>
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <IconPhoto size={16} className="text-muted-foreground shrink-0" />
            <h3 className="text-sm font-semibold">{storyboard.title}</h3>
          </div>
          {storyboard.description ? (
            <p className="text-muted-foreground text-sm leading-snug">
              {storyboard.description}
            </p>
          ) : null}
        </div>
        <div className={cn('shrink-0 flex-col items-end gap-1', isSimple ? 'hidden sm:flex' : 'flex')}>
          <span className="bg-muted text-muted-foreground rounded px-2 py-1 text-xs">
            {formatCount(storyboard.frames.length, 'frame', 'frames')}
          </span>
          <span className="bg-muted text-muted-foreground rounded px-2 py-1 text-xs">
            {formatCount(sourceCount, 'video', 'videos')}
          </span>
        </div>
      </header>
      <div className={cn('space-y-4', isSimple ? 'p-2.5' : 'p-3')}>
        {groups.map((group) => (
          <section key={group.key} className="space-y-2">
            {sourceCount > 1 ? (
              <div className="text-muted-foreground flex min-w-0 items-center justify-between gap-3 text-xs">
                <div className="flex min-w-0 items-center gap-2">
                  <IconVideo size={14} className="shrink-0" />
                  <span className="truncate">{group.label}</span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span>{formatCount(group.frames.length, 'frame', 'frames')}</span>
                  {group.videoUrl ? (
                    <a
                      href={group.videoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-foreground focus-visible:ring-ring inline-flex min-h-8 items-center gap-1 focus-visible:outline-none focus-visible:ring-2"
                    >
                      <IconExternalLink size={14} />
                      Source
                    </a>
                  ) : null}
                </div>
              </div>
            ) : null}
            <div
              className={cn(
                'grid gap-3',
                isSimple
                  ? 'grid-cols-[repeat(auto-fit,minmax(min(100%,150px),1fr))]'
                  : 'grid-cols-[repeat(auto-fit,minmax(min(100%,220px),1fr))]'
              )}
            >
              {group.frames.map((frame, index) => (
                <StoryboardFrameCard
                  key={frame.id || `${group.key}-${frame.timestamp}-${index}`}
                  frame={frame}
                  simple={isSimple}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
      {!isSimple && storyboard.videoUrl ? (
        <footer className="border-border border-t px-3 py-2">
          <a
            href={storyboard.videoUrl}
            target="_blank"
            rel="noreferrer"
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-xs"
          >
            <IconVideo size={14} />
            Open source video
          </a>
        </footer>
      ) : null}
    </section>
  )
}

// --------------

export const VideoTimestamp: React.FC<VideoTimestampProps> = ({ data, className }) => {
  const frame = normalizeTimestamp(data)
  if (!frame) return null

  return (
    <section className={cn('my-4', className)}>
      <StoryboardFrameCard frame={frame} compact />
    </section>
  )
}
